import React from 'react';
import LegalPage, { H2, P } from './LegalPage';

const NAVY = '#111936';
const MUTED = '#6b7280';

const Example = ({ children }) => (
  <li
    style={{
      listStyle: 'none',
      margin: '6px 0',
      padding: '8px 12px',
      background: '#f6f7fb',
      border: '1px solid #e4e7ef',
      borderRadius: '10px',
      fontStyle: 'italic',
      color: '#3a4256',
      fontSize: '14px',
    }}
  >
    “{children}”
  </li>
);

const Section = ({ emoji, title, blurb, examples }) => (
  <div style={{ margin: '22px 0' }}>
    <h2 style={{ color: NAVY, fontSize: '19px', margin: '0 0 4px' }}>
      <span style={{ marginRight: '8px' }}>{emoji}</span>
      {title}
    </h2>
    <p style={{ margin: '0 0 10px', color: MUTED }}>{blurb}</p>
    <ul style={{ margin: 0, padding: 0 }}>
      {examples.map((e, i) => (
        <Example key={i}>{e}</Example>
      ))}
    </ul>
  </div>
);

const Note = ({ children }) => (
  <div
    style={{
      margin: '14px 0',
      padding: '12px 14px',
      background: '#fff8e6',
      border: '1px solid #f5e3b3',
      borderRadius: '10px',
      color: '#5a4a18',
      fontSize: '14px',
    }}
  >
    {children}
  </div>
);

const ConnectorGuide = () => (
  <LegalPage title="Influencer connector — quick guide">
    <P>
      Connect Influencer.in to your AI assistant (Claude) and just ask in plain
      words. Everything is scoped to your brand and your logged-in account. Here
      is everything you can do, with example prompts you can copy.
    </P>

    <Section
      emoji="🔍"
      title="Find influencers"
      blurb="Discover vetted creators across Instagram & YouTube by niche, location, follower count, engagement rate, average views, gender, or language. Ask for one creator's full profile any time."
      examples={[
        'Find top 20 fashion influencers in Mumbai with over 3% engagement',
        'Show me food creators in Delhi with 100k+ followers',
        'Find Tamil-speaking tech YouTubers with high average views',
        'Gaming streamers under 100k followers',
        'Tell me more about [creator name]',
      ]}
    />

    <Section
      emoji="🎯"
      title="Find creators by their AUDIENCE's interest"
      blurb="This finds creators whose FOLLOWERS care about a topic or product — not the creator's own niche. Just say it in plain words (a product, brand, or theme); it's matched to the audience-interest data automatically and ranked by how strongly each creator's audience indexes on it (not by follower count), so you get genuine niche reach instead of the biggest celebrities."
      examples={[
        'Influencers whose audience is interested in PS5',
        'Creators whose followers love skincare',
        'YouTubers with an audience into luxury cars',
        'Find creators whose audience plays shooter games',
        'Audience interested in gaming, with at least 40% affinity',
      ]}
    />
    <Note>
      <strong>Tip:</strong> YouTube has the richest first-party audience data
      (granular interests like “Console Gamers”, “Esports Fans”), so YouTube
      gives the most precise audience targeting. Instagram uses broader
      categories (e.g. “Gaming”). Each result shows the matched interest and the
      audience’s affinity %.
    </Note>

    <Section
      emoji="📣"
      title="Review your campaigns"
      blurb="List your campaigns, then dig into any one — performance, and the shortlisted creators (roster) with their deliverables, prices and approval status."
      examples={[
        'List my campaigns',
        'How did the Summer Launch campaign perform?',
        'Who is shortlisted on my Diwali campaign?',
        'Show the approved creators on that campaign with their prices',
      ]}
    />

    <Section
      emoji="📊"
      title="Track & compare competitors"
      blurb="See who your competitors are, compare their performance, and look at their campaigns, top posts, the creators amplifying them, posting trends and audience overlap — plus the AI competitive-intelligence report."
      examples={[
        'Who are my competitors?',
        'Which competitor is doing best on Instagram?',
        'Compare my competitors by engagement rate',
        'What campaigns is [competitor] running, and how are they doing?',
        "Show me [competitor]'s top posts",
        'Which creators are amplifying [competitor]?',
        'Give me the intelligence report for [competitor]',
      ]}
    />

    <Section
      emoji="📑"
      title="Scope competitor analysis to a saved report"
      blurb="A brand can have several saved competitor reports (each with its own competitor set and date window). Pick one and the analysis stays scoped to it; or stay on the general (all competitors) view."
      examples={[
        'List my competitor reports',
        'Use my Q2 Skincare report for the rest of this chat',
        'Go back to all competitors',
      ]}
    />

    <Section
      emoji="✍️"
      title="Create a competitor report"
      blurb="Set up a new competitor-analysis report from chat — it appears in your dashboard just like one created in the app. (You'll be asked to confirm before it's created.)"
      examples={[
        'Create a competitor report called "Beverage rivals" tracking thumsup, sprite and pepsi on Instagram',
      ]}
    />

    <H2>Pick once, and it stays focused — no mixed data</H2>
    <P>
      When you ask about campaigns or competitors without naming one, the
      connector first shows you the list and lets you choose. After you pick a
      campaign (or a competitor / report), every follow-up question in that chat
      stays scoped to <strong>that one</strong> — it never blends numbers across
      different campaigns or competitors. You can switch any time by saying
      “use a different campaign” (it'll offer “same as before, or pick another”).
    </P>
    <P style={{ color: MUTED }}>
      Example flow: “Show my campaigns” → you pick <em>Diwali Launch</em> → “What
      was the engagement?” → “Who were the top creators?” — all answers stay on
      the Diwali Launch campaign until you choose another.
    </P>

    <H2>Need help inside a chat?</H2>
    <P>
      Just ask <strong>“what can you do?”</strong> — the connector will point you
      back to this guide.
    </P>
  </LegalPage>
);

export default ConnectorGuide;
